import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { analyticsAPI, favoritesAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { 
  Target, 
  Calendar,
  MapPin,
  DollarSign,
  TrendingUp,
  ArrowLeft,
  Calculator,
  CheckCircle2,
  AlertCircle,
  Sparkles
} from 'lucide-react';
import { formatDate } from '../lib/utils';
import { HelpButton } from '../components/HelpButton';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};

export const WhatIfPlanner = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [selectedEvents, setSelectedEvents] = useState([]);
  const [projection, setProjection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchEvents();
  }, [user, navigate]);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const response = await favoritesAPI.getAll();
      setEvents(response.data);
    } catch (error) {
      toast.error('Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleEvent = (eventId) => {
    setSelectedEvents(prev => 
      prev.includes(eventId) 
        ? prev.filter(id => id !== eventId)
        : [...prev, eventId]
    );
    setProjection(null); // Clear projection when selection changes
  };

  const handleSelectAll = () => {
    if (selectedEvents.length === events.length) {
      setSelectedEvents([]);
    } else {
      setSelectedEvents(events.map(e => e.id));
    }
    setProjection(null);
  };

  const handleCalculate = async () => {
    if (selectedEvents.length === 0) {
      toast.error('Please select at least one event');
      return;
    }

    try {
      setCalculating(true);
      const response = await analyticsAPI.whatIf(selectedEvents);
      setProjection(response.data);
    } catch (error) {
      toast.error('Failed to calculate projection');
    } finally {
      setCalculating(false);
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-[#FFFFFF] py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button variant="ghost" onClick={() => navigate('/analytics')} className="p-2">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-2">
            <div>
              <h1 className="text-3xl font-bold text-[#1E3A5F] font-['Outfit']">What-If Planner</h1>
              <p className="text-gray-600 mt-1">Model different event scenarios to optimize your season</p>
            </div>
            <HelpButton tutorialId="whatif" />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Event Selection */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl border-2 border-[#1E3A5F] p-6 shadow-hard">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold text-[#1E3A5F] flex items-center gap-2">
                  <Calendar className="w-5 h-5" />
                  Select Events for Scenario
                </h2>
                <Button variant="outline" size="sm" onClick={handleSelectAll}>
                  {selectedEvents.length === events.length ? 'Deselect All' : 'Select All'}
                </Button>
              </div>

              {loading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1E3A5F]"></div>
                </div>
              ) : events.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Calendar className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No saved events found</p>
                  <p className="text-sm">Save some events first to plan your season</p>
                </div>
              ) : (
                <div className="space-y-3 max-h-[500px] overflow-y-auto">
                  {events.map((event) => (
                    <div
                      key={event.id}
                      className={`flex items-center gap-4 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                        selectedEvents.includes(event.id)
                          ? 'border-[#1E3A5F] bg-[#FFEDDF]'
                          : 'border-gray-200 hover:border-[#1E3A5F]/50'
                      }`}
                      onClick={() => handleToggleEvent(event.id)}
                      data-testid={`event-${event.id}`}
                    >
                      <Checkbox
                        checked={selectedEvents.includes(event.id)}
                        onCheckedChange={() => handleToggleEvent(event.id)}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-[#1E3A5F] truncate">{event.title}</p>
                        <div className="flex items-center gap-4 text-sm text-gray-500">
                          {event.date && (
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {formatDate(event.date)}
                            </span>
                          )}
                          {event.city && (
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {event.city}, {event.state}
                            </span>
                          )}
                        </div>
                      </div>
                      {event.booth_cost > 0 && (
                        <div className="text-right">
                          <p className="text-sm text-gray-500">Booth Fee</p>
                          <p className="font-medium text-[#1E3A5F]">${event.booth_cost}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              <div className="mt-6 pt-4 border-t">
                <Button 
                  onClick={handleCalculate}
                  disabled={selectedEvents.length === 0 || calculating}
                  className="w-full btn-primary"
                  data-testid="calculate-btn"
                >
                  {calculating ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                      Calculating...
                    </>
                  ) : (
                    <>
                      <Calculator className="w-4 h-4 mr-2" />
                      Calculate Projection ({selectedEvents.length} events)
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>

          {/* Projection Results */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl border-2 border-[#1E3A5F] p-6 shadow-hard sticky top-8">
              <h2 className="text-lg font-semibold text-[#1E3A5F] mb-4 flex items-center gap-2">
                <Target className="w-5 h-5 text-[#1E3A5F]" />
                Scenario Projection
              </h2>

              {!projection ? (
                <div className="text-center py-8 text-gray-500">
                  <Sparkles className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>Select events and click Calculate</p>
                  <p className="text-sm">to see projected results</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Summary */}
                  <div className="space-y-3">
                    <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                      <span className="text-gray-600">Events Selected</span>
                      <span className="font-bold text-[#1E3A5F]">{projection.scenario_summary.event_count}</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                      <span className="text-gray-600">Total Booth Fees</span>
                      <span className="font-bold text-red-600">{formatCurrency(projection.scenario_summary.total_booth_fees)}</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                      <span className="text-gray-600">Projected Revenue</span>
                      <span className="font-bold text-blue-600">{formatCurrency(projection.scenario_summary.projected_revenue)}</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                      <span className="text-gray-600">Avg Revenue/Event</span>
                      <span className="font-bold text-gray-700">{formatCurrency(projection.avg_revenue_per_event)}</span>
                    </div>
                  </div>

                  {/* Net Result */}
                  <div className={`p-4 rounded-lg ${
                    projection.scenario_summary.projected_profit >= 0 
                      ? 'bg-green-100 border-2 border-green-400' 
                      : 'bg-red-100 border-2 border-red-400'
                  }`}>
                    <div className="flex items-center justify-between">
                      <span className="font-medium">Projected Profit</span>
                      {projection.scenario_summary.projected_profit >= 0 ? (
                        <CheckCircle2 className="w-5 h-5 text-green-600" />
                      ) : (
                        <AlertCircle className="w-5 h-5 text-red-600" />
                      )}
                    </div>
                    <p className={`text-2xl font-bold ${
                      projection.scenario_summary.projected_profit >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {formatCurrency(projection.scenario_summary.projected_profit)}
                    </p>
                    {projection.scenario_summary.total_booth_fees > 0 && (
                      <p className={`text-sm ${
                        projection.scenario_summary.projected_profit >= 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {projection.scenario_summary.projected_profit >= 0 ? '+' : ''}
                        {Math.round((projection.scenario_summary.projected_profit / projection.scenario_summary.total_booth_fees) * 100)}% ROI
                      </p>
                    )}
                  </div>

                  {/* Individual Event Projections */}
                  <div>
                    <h3 className="text-sm font-semibold text-gray-600 mb-2">Event Breakdown</h3>
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {projection.events && projection.events.map((proj) => (
                        <div key={proj.event_id} className="p-3 bg-gray-50 rounded-lg text-sm">
                          <div className="flex justify-between items-start">
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-[#1E3A5F] truncate">{proj.title}</p>
                              {!proj.has_historical_data && (
                                <p className="text-xs text-amber-600">* Using average estimates</p>
                              )}
                            </div>
                            <p className={`font-bold ${proj.projected_profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {formatCurrency(proj.projected_profit)}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Insights */}
                  <div className="p-4 bg-[#FFEDDF] rounded-lg">
                    <h3 className="text-sm font-semibold text-[#1E3A5F] mb-2">Insights</h3>
                    <ul className="text-sm text-gray-700 space-y-1">
                      {projection.scenario_summary.projected_profit > 0 ? (
                        <li>✓ This scenario is projected to be profitable</li>
                      ) : (
                        <li>⚠ This scenario may result in a loss</li>
                      )}
                      {projection.events && projection.events.filter(p => !p.has_historical_data).length > 0 && (
                        <li>ℹ {projection.events.filter(p => !p.has_historical_data).length} events use estimated averages</li>
                      )}
                      {projection.scenario_summary.total_booth_fees > 0 && 
                       Math.round((projection.scenario_summary.projected_profit / projection.scenario_summary.total_booth_fees) * 100) > 20 && (
                        <li>🎯 Strong ROI projected ({Math.round((projection.scenario_summary.projected_profit / projection.scenario_summary.total_booth_fees) * 100)}%)</li>
                      )}
                    </ul>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WhatIfPlanner;
